import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  ArrowLeft,
  Check,
  Sparkles,
} from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { WHATSAPP_URL } from "../lib/constants";
import { useT } from "../i18n";

const HERO_IMAGE =
  "https://customer-assets-0z36b82j.emergentagent.net/job_movimento-saude-1/artifacts/jggmgwlz_treino%20personalizado.jpeg";

const fade = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.75, ease: "easeOut" },
};

export default function TreinoClinicoPage() {
  const { t } = useT();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const howItems = t("treinoPage.howItems");
  const supportItems = t("treinoPage.supportItems");

  return (
    <div className="App bg-[#FEFEFE]" data-testid="treino-page">
      <Navbar />

      {/* ═══════ HERO ═══════ */}
      <section
        data-testid="treino-hero"
        className="relative min-h-[78vh] lg:min-h-[80vh] w-full overflow-hidden bg-[#0F1A17]"
      >
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="Treino Clínico Personalizado"
            className="w-full h-full object-cover object-[55%_40%] opacity-[0.9]"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F1A17]/75 via-[#0F1A17]/45 to-[#0F1A17]/10" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F1A17]/45 via-transparent to-transparent" />
        </div>

        <div className="relative z-10 min-h-[78vh] lg:min-h-[80vh] flex flex-col">
          <div className="flex-1 flex items-end pb-20 md:pb-28 pt-36 md:pt-44">
            <div className="w-full max-w-[1200px] mx-auto px-5 md:px-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: "easeOut" }}
                className="max-w-[860px]"
              >
                <Link
                  to="/"
                  data-testid="treino-back"
                  className="inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase font-semibold text-white/70 hover:text-[#EE8984] transition-colors mb-10"
                >
                  <ArrowLeft size={14} />
                  {t("treinoPage.back")}
                </Link>

                <div className="flex items-center gap-3 mb-8">
                  <span className="h-px w-10 bg-[#EE8984]" />
                  <span className="text-[11px] tracking-[0.32em] uppercase font-semibold text-[#F7DEDA]">
                    {t("treinoPage.eyebrow")}
                  </span>
                </div>

                <h1 className="font-serif text-[34px] sm:text-[44px] lg:text-[56px] xl:text-[64px] leading-[1.1] tracking-tight text-white font-light">
                  {t("treinoPage.heroTitle")}
                </h1>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ INTRO ═══════ */}
      <section className="relative py-24 md:py-36 bg-[#FEFEFE]">
        <div className="max-w-[1200px] mx-auto px-5 md:px-6 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20">
          <motion.div {...fade} className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-7">
              <span className="h-px w-10 bg-[#A3BD98]" />
              <span className="text-[11px] tracking-[0.32em] uppercase font-semibold text-[#ADBDAD]">
                {t("treinoPage.introEyebrow")}
              </span>
            </div>
          </motion.div>
          <motion.div {...fade} className="lg:col-span-8 space-y-7">
            <p className="font-serif text-[22px] md:text-[28px] lg:text-[32px] leading-[1.4] tracking-tight text-[#2A322C] font-light">
              {t("treinoPage.introBody1")}
            </p>
            <p className="text-[16px] md:text-[17px] text-[#5C6660] leading-[1.85] max-w-[680px]">
              {t("treinoPage.introBody2")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ═══════ COMO TRABALHAMOS ═══════ */}
      <section className="relative py-24 md:py-36 bg-[#EEF2E9]">
        <div className="max-w-[1200px] mx-auto px-5 md:px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <motion.div {...fade} className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-7">
              <span className="h-px w-10 bg-[#A3BD98]" />
              <span className="text-[11px] tracking-[0.32em] uppercase font-semibold text-[#ADBDAD]">
                {t("treinoPage.howEyebrow")}
              </span>
            </div>
            <h2 className="font-serif text-[32px] md:text-[40px] lg:text-[46px] leading-[1.15] tracking-tight text-[#2A322C] font-semibold">
              {t("treinoPage.howTitle")}
            </h2>
            <p className="mt-6 text-[16px] md:text-[17px] text-[#5C6660] leading-[1.85] max-w-[480px]">
              {t("treinoPage.howLead")}
            </p>
          </motion.div>

          <motion.ul {...fade} className="lg:col-span-7 space-y-5">
            {howItems.map((item, i) => (
              <li
                key={i}
                data-testid={`treino-how-${i}`}
                className="group flex items-start gap-5 bg-white/70 backdrop-blur-sm border border-[#A3BD98]/15 rounded-[12px] px-7 py-6 hover:bg-white hover:border-[#A3BD98]/35 hover:-translate-y-[2px] transition-all duration-300 shadow-[0_6px_18px_-14px_rgba(42,50,44,0.12)]"
              >
                <span className="shrink-0 mt-[6px] w-5 h-5 rounded-full bg-[#A3BD98] flex items-center justify-center text-white">
                  <Check size={12} strokeWidth={3} />
                </span>
                <span className="text-[16px] md:text-[17px] text-[#2A322C] leading-[1.6] font-medium">
                  {item}
                </span>
              </li>
            ))}
          </motion.ul>
        </div>
      </section>

      {/* ═══════ ABORDAGEM INTEGRADA (dark editorial) ═══════ */}
      <section className="relative py-24 md:py-36 bg-[#0F1A17] overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(163,189,152,0.12),transparent_55%)] pointer-events-none"
        />
        <div className="relative max-w-[1200px] mx-auto px-5 md:px-6">
          <motion.div {...fade} className="max-w-3xl">
            <div className="flex items-center gap-3 mb-7">
              <span className="h-px w-10 bg-[#A3BD98]/70" />
              <span className="text-[11px] tracking-[0.32em] uppercase font-semibold text-[#A3BD98]/80">
                {t("treinoPage.integratedEyebrow")}
              </span>
            </div>
            <h2 className="font-serif text-[32px] md:text-[40px] lg:text-[48px] leading-[1.15] tracking-tight text-white font-semibold">
              {t("treinoPage.integratedTitle")}
            </h2>
            <p className="mt-8 text-[18px] md:text-[20px] text-white/80 leading-[1.85] font-light">
              {t("treinoPage.integratedBody1")}
            </p>
            <p className="mt-6 text-[16px] md:text-[17px] text-white/60 leading-[1.85]">
              {t("treinoPage.integratedBody2")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ═══════ ACOMPANHAMENTO ESPECIALIZADO ═══════ */}
      <section className="relative py-24 md:py-36 bg-[#FEFEFE]">
        <div className="max-w-[1200px] mx-auto px-5 md:px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <motion.div {...fade} className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-7">
              <span className="h-px w-10 bg-[#EE8984]" />
              <span className="text-[11px] tracking-[0.32em] uppercase font-semibold text-[#CF8376]">
                {t("treinoPage.supportEyebrow")}
              </span>
            </div>
            <h2 className="font-serif text-[30px] md:text-[38px] lg:text-[44px] leading-[1.15] tracking-tight text-[#2A322C] font-semibold">
              {t("treinoPage.supportTitle")}
            </h2>
            <p className="mt-6 text-[16px] md:text-[17px] text-[#5C6660] leading-[1.85] max-w-[480px]">
              {t("treinoPage.supportLead")}
            </p>
            <p className="mt-6 text-[11px] tracking-[0.28em] uppercase font-semibold text-[#5C6660]">
              {t("treinoPage.supportSubhead")}
            </p>
          </motion.div>

          <motion.div {...fade} className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4 self-center">
            {supportItems.map((item, i) => (
              <div
                key={i}
                data-testid={`treino-support-${i}`}
                className="group relative border border-[#E4E9DE] rounded-[14px] px-7 py-8 hover:border-[#A3BD98]/50 hover:bg-[#EEF2E9]/40 transition-all duration-300"
              >
                <span className="absolute top-5 right-5 text-[11px] tracking-[0.22em] uppercase font-semibold text-[#ADBDAD]">
                  0{i + 1}
                </span>
                <span className="font-serif text-[22px] md:text-[26px] text-[#2A322C] font-semibold leading-[1.2] tracking-tight block">
                  {item}
                </span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ═══════ OBJETIVO ═══════ */}
      <section className="relative py-24 md:py-36 bg-[#EEF2E9]">
        <motion.div {...fade} className="max-w-[900px] mx-auto px-5 md:px-6 text-center">
          <div className="inline-flex items-center gap-3 mb-8">
            <span className="h-px w-10 bg-[#A3BD98]" />
            <span className="text-[11px] tracking-[0.32em] uppercase font-semibold text-[#ADBDAD]">
              {t("treinoPage.goalEyebrow")}
            </span>
            <span className="h-px w-10 bg-[#A3BD98]" />
          </div>
          <Sparkles size={28} strokeWidth={1.5} className="text-[#A3BD98] mx-auto mb-6" />
          <h2 className="font-serif text-[28px] md:text-[36px] lg:text-[42px] leading-[1.25] tracking-tight text-[#2A322C] font-semibold">
            {t("treinoPage.goalTitle")}
          </h2>
          <p className="mt-7 text-[17px] md:text-[19px] text-[#5C6660] leading-[1.85] font-light max-w-2xl mx-auto">
            {t("treinoPage.goalBody")}
          </p>
        </motion.div>
      </section>

      {/* ═══════ CTA FINAL ═══════ */}
      <section className="relative py-24 md:py-32 bg-[#FEFEFE] border-t border-[#E4E9DE]">
        <motion.div {...fade} className="max-w-[900px] mx-auto px-5 md:px-6 text-center">
          <p className="text-[11px] tracking-[0.32em] uppercase font-semibold text-[#ADBDAD] mb-6">
            {t("treinoPage.ctaEyebrow")}
          </p>
          <h3 className="font-serif text-[26px] md:text-[34px] lg:text-[40px] leading-[1.25] tracking-tight text-[#2A322C] font-light max-w-2xl mx-auto">
            {t("treinoPage.ctaTitle")}
          </h3>
          <div className="mt-10 flex items-center justify-center">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="treino-cta"
              className="group inline-flex items-center gap-2 bg-[#EE8984] text-white rounded-full px-9 py-[18px] text-[14px] font-semibold tracking-wide hover:bg-[#CF8376] hover:scale-[1.02] hover:shadow-[0_16px_40px_-12px_rgba(238,137,132,0.55)] transition-all duration-300"
            >
              {t("treinoPage.ctaButton")}
              <ArrowUpRight
                size={17}
                className="group-hover:rotate-45 transition-transform duration-300"
              />
            </a>
          </div>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
}
